import React, { useState, useEffect, useCallback, useMemo } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import { SetupFilterBar } from '../components';
import { useApi } from '../../../api/useApi';

interface SetupPricingLabProps {
  brand: BrandConfig;
  Icons: IconsType;
}

type PricingLabTab = 'originalSkus' | 'dimensionalModel' | 'skuTranslator' | 'anomalies' | 'optimizationGuide' | 'summary';

// ── API Response Types ──

interface SkuRow {
  id: string;
  basePrice: string;
  nonLawFirmPrice: string | null;
  affiliatePrice: string | null;
  priceType: string;
  isBundleDiscount: boolean;
  pricingItemName: string;
  unitDescription: string | null;
  productCode: string | null;
  productName: string | null;
  zoneName: string | null;
  zoneCode: string | null;
  sizeTierName: string | null;
  sizeTierCode: string | null;
  serviceLevelName: string | null;
  serviceLevelCode: string | null;
}

interface MatrixPrice {
  id: string;
  basePrice: string;
  nonLawFirmPrice: string | null;
  priceType: string;
  isBundleDiscount: boolean;
  pricingItemName: string;
  zoneCode: string | null;
  zoneName: string | null;
  sizeTierCode: string | null;
  sizeTierName: string | null;
  serviceLevelCode: string | null;
  serviceLevelName: string | null;
  computedPrice: number | null;
  match: boolean;
}

interface MatrixResponse {
  product: { id: string; code: string; name: string };
  basePrice: number | null;
  adders: Record<string, Record<string, number>>;
  dimensionalPrices: MatrixPrice[];
  ancillaryPrices: MatrixPrice[];
  matchCount: number;
  totalDimensional: number;
}

interface SummaryRow {
  productCode: string;
  productName: string;
  totalRows: number;
  dimensionalRows: number;
  ancillaryRows: number;
  perUnitRows: number;
  bundleDiscountRows: number;
  zoneAdditive: boolean;
  sizeAdditive: boolean;
  levelAdditive: boolean;
  modelType: string;
  adderCount: number;
  optimizedRows: number;
  configItems: number;
  compressionLabel: string;
}

interface SummaryResponse {
  data: SummaryRow[];
  stats: {
    totalRows: number;
    totalDimensional: number;
    totalAncillary: number;
    totalPerUnit: number;
    totalAdders: number;
    totalProducts: number;
    totalBundleDiscount: number;
    totalOptimizedDimensional: number;
    totalUnoptimizedDimensional: number;
    totalConfigItems: number;
    totalLookupRows: number;
    productsWithAdders: number;
    productsWithoutAdders: number;
    productsFlatOnly: number;
  };
}

interface AdderRow {
  id: string;
  productId: string;
  productCode: string;
  productName: string;
  dimension: string;
  dimensionValue: string;
  adderAmount: string;
}

interface AddersByProduct {
  [productCode: string]: {
    productName: string;
    basePrice: number | null;
    adders: Record<string, Record<string, number>>; // dimension → value → amount
  };
}

interface AnomalyRow {
  productCode: string | null;
  productName: string | null;
  zoneName: string | null;
  zoneCode: string | null;
  sizeTierName: string | null;
  sizeTierCode: string | null;
  serviceLevelName: string | null;
  serviceLevelCode: string | null;
  actualPrice: number;
  expectedPrice: number;
  varianceDollars: number;
  variancePercent: number;
  pricingItemName: string;
  lastModifiedSource: string | null;
}

interface AnomalyPattern {
  description: string;
  count: number;
  affectedRows: string[];
}

interface AnomalyResponse {
  mismatches: AnomalyRow[];
  stats: {
    totalMismatches: number;
    productsAffected: number;
    totalDimensionalRows: number;
    medianMatchedDate: string | null;
    medianMismatchDate: string | null;
  };
  patterns: AnomalyPattern[];
}

// ── Component ──

const SetupPricingLab: React.FC<SetupPricingLabProps> = ({ brand, Icons }) => {
  const { get } = useApi();
  const [activeTab, setActiveTab] = useState<PricingLabTab>('originalSkus');
  const [isLoading, setIsLoading] = useState(true);

  // Tab 1: Original SKUs
  const [skus, setSkus] = useState<SkuRow[]>([]);
  const [search, setSearch] = useState('');
  const [productFilter, setProductFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // Tab 2: Dimensional Model
  const [matrixData, setMatrixData] = useState<MatrixResponse | null>(null);
  const [selectedProduct, setSelectedProduct] = useState('county-recorder');
  const [editableAdders, setEditableAdders] = useState<Record<string, Record<string, number>>>({});

  // Tab 3: SKU Translator
  const [adders, setAdders] = useState<AdderRow[]>([]);
  const [selectedSkuId, setSelectedSkuId] = useState<string | null>(null);
  const [translatorSearch, setTranslatorSearch] = useState('');
  const [translatorProductFilter, setTranslatorProductFilter] = useState('all');
  const [showAllSkus, setShowAllSkus] = useState(false);

  // Tab 4: Anomalies
  const [anomalyData, setAnomalyData] = useState<AnomalyResponse | null>(null);
  const [anomalySortField, setAnomalySortField] = useState<'product' | 'variance'>('product');

  // Tab 5: Summary
  const [summaryData, setSummaryData] = useState<SummaryResponse | null>(null);

  // Product options derived from SKU data
  const productOptions = React.useMemo(() => {
    const products = new Map<string, string>();
    skus.forEach(s => {
      if (s.productCode && s.productName) {
        products.set(s.productCode, s.productName);
      }
    });
    return Array.from(products.entries()).map(([code, name]) => ({ value: code, label: name }));
  }, [skus]);

  // ── Data Fetching ──

  const fetchSkus = useCallback(async () => {
    try {
      const data = await get<{ data: SkuRow[]; total: number }>('/api/admin/setup/pricing-lab/skus');
      setSkus(data.data || []);
    } catch (error) {
      console.error('Failed to fetch SKUs:', error);
    }
  }, [get]);

  const fetchMatrix = useCallback(async (productCode: string) => {
    try {
      const data = await get<MatrixResponse>(`/api/admin/setup/pricing-lab/matrix/${productCode}`);
      setMatrixData(data);
      setEditableAdders(JSON.parse(JSON.stringify(data.adders)));
    } catch (error) {
      console.error('Failed to fetch matrix:', error);
    }
  }, [get]);

  const fetchAdders = useCallback(async () => {
    try {
      const data = await get<{ data: AdderRow[] }>('/api/admin/setup/pricing-lab/adders');
      setAdders(data.data || []);
    } catch (error) {
      console.error('Failed to fetch adders:', error);
    }
  }, [get]);

  const fetchAnomalies = useCallback(async () => {
    try {
      const data = await get<AnomalyResponse>('/api/admin/setup/pricing-lab/anomalies');
      setAnomalyData(data);
    } catch (error) {
      console.error('Failed to fetch anomalies:', error);
    }
  }, [get]);

  const fetchSummary = useCallback(async () => {
    try {
      const data = await get<SummaryResponse>('/api/admin/setup/pricing-lab/summary');
      setSummaryData(data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  }, [get]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      await Promise.all([fetchSkus(), fetchSummary(), fetchAdders(), fetchAnomalies()]);
      await fetchMatrix(selectedProduct);
      setIsLoading(false);
    };
    load();
  }, [fetchSkus, fetchSummary, fetchAdders, fetchAnomalies, fetchMatrix, selectedProduct]);

  // ── Filtering ──

  const filteredSkus = React.useMemo(() => {
    let filtered = [...skus];

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(s =>
        s.pricingItemName.toLowerCase().includes(searchLower) ||
        s.productName?.toLowerCase().includes(searchLower) ||
        s.zoneName?.toLowerCase().includes(searchLower) ||
        s.serviceLevelName?.toLowerCase().includes(searchLower),
      );
    }

    if (productFilter !== 'all') {
      filtered = filtered.filter(s => s.productCode === productFilter);
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(s => s.priceType === typeFilter);
    }

    return filtered;
  }, [skus, search, productFilter, typeFilter]);

  // ── Stats ──

  const skuStats = React.useMemo(() => {
    const dimensional = skus.filter(s => s.priceType === 'dimensional' && !s.isBundleDiscount).length;
    const ancillary = skus.filter(s => s.priceType === 'ancillary').length;
    const perUnit = skus.filter(s => s.priceType === 'per-unit').length;
    const bundle = skus.filter(s => s.isBundleDiscount).length;
    return { total: skus.length, dimensional, ancillary, perUnit, bundle };
  }, [skus]);

  // ── Adders by Product (for SKU Translator) ──

  const addersByProduct = useMemo<AddersByProduct>(() => {
    const result: AddersByProduct = {};

    for (const adder of adders) {
      if (!result[adder.productCode]) {
        result[adder.productCode] = {
          productName: adder.productName,
          basePrice: null,
          adders: {},
        };
      }
      const entry = result[adder.productCode];
      if (!entry.adders[adder.dimension]) {
        entry.adders[adder.dimension] = {};
      }
      entry.adders[adder.dimension][adder.dimensionValue] = Number(adder.adderAmount);
    }

    // Compute base price for each product: find the SKU where all adder dimensions = 0
    for (const [productCode, entry] of Object.entries(result)) {
      const baseDimValues: Record<string, string> = {};
      for (const [dim, values] of Object.entries(entry.adders)) {
        for (const [value, amount] of Object.entries(values)) {
          if (amount === 0) baseDimValues[dim] = value;
        }
      }

      const baseSku = skus.find(s => {
        if (s.productCode !== productCode || s.priceType !== 'dimensional' || s.isBundleDiscount) return false;
        const matchesZone = !baseDimValues.zone || s.zoneCode === baseDimValues.zone;
        const matchesSize = !baseDimValues.size || s.sizeTierCode === baseDimValues.size;
        const matchesLevel = !baseDimValues.level || s.serviceLevelCode === baseDimValues.level;
        return matchesZone && matchesSize && matchesLevel;
      });

      entry.basePrice = baseSku ? Number(baseSku.basePrice) : null;
    }

    return result;
  }, [adders, skus]);

  // ── Translator Filtered SKUs ──

  const translatorFilteredSkus = useMemo(() => {
    let filtered = [...skus];

    // By default show only dimensional non-bundle SKUs
    if (!showAllSkus) {
      filtered = filtered.filter(s => s.priceType === 'dimensional' && !s.isBundleDiscount);
    }

    if (translatorSearch) {
      const searchLower = translatorSearch.toLowerCase();
      filtered = filtered.filter(s =>
        s.pricingItemName.toLowerCase().includes(searchLower) ||
        s.productName?.toLowerCase().includes(searchLower) ||
        s.zoneName?.toLowerCase().includes(searchLower) ||
        s.sizeTierName?.toLowerCase().includes(searchLower) ||
        s.serviceLevelName?.toLowerCase().includes(searchLower),
      );
    }

    if (translatorProductFilter !== 'all') {
      filtered = filtered.filter(s => s.productCode === translatorProductFilter);
    }

    return filtered;
  }, [skus, translatorSearch, translatorProductFilter, showAllSkus]);

  // ── Interactive Adder Editing ──

  const handleAdderChange = (dimension: string, value: string, newAmount: number) => {
    setEditableAdders(prev => ({
      ...prev,
      [dimension]: {
        ...prev[dimension],
        [value]: newAmount,
      },
    }));
  };

  // Compute prices with editable adders
  const computedMatrixPrices = React.useMemo(() => {
    if (!matrixData) return [];

    const basePrice = matrixData.basePrice;
    if (basePrice === null) return matrixData.dimensionalPrices;

    return matrixData.dimensionalPrices.map(p => {
      const zoneAdder = editableAdders.zone?.[p.zoneCode ?? ''] ?? 0;
      const sizeAdder = editableAdders.size?.[p.sizeTierCode ?? ''] ?? 0;
      const levelAdder = editableAdders.level?.[p.serviceLevelCode ?? ''] ?? 0;
      const computedPrice = basePrice + zoneAdder + sizeAdder + levelAdder;
      const match = Math.abs(computedPrice - Number(p.basePrice)) < 0.01;

      return { ...p, computedPrice, match };
    });
  }, [matrixData, editableAdders]);

  // ── Row background color by type ──

  const getRowBg = (row: SkuRow): string => {
    if (row.isBundleDiscount || Number(row.basePrice) < 0) return '#FEF2F2'; // light red
    if (row.priceType === 'ancillary') return '#F9FAFB'; // light gray
    if (row.priceType === 'per-unit') return '#EFF6FF'; // light blue
    return '';
  };

  // ── Tab Navigation ──

  const tabs: { key: PricingLabTab; label: string }[] = [
    { key: 'originalSkus', label: 'Original SKUs' },
    { key: 'dimensionalModel', label: 'Dimensional Model' },
    { key: 'skuTranslator', label: 'SKU Translator' },
    { key: 'anomalies', label: 'Price Verification' },
    { key: 'optimizationGuide', label: 'Optimization Guide' },
    { key: 'summary', label: 'Comparison Summary' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: brand.colors.primary, borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Tab Navigation */}
      <div
        className="flex border-b mb-6"
        style={{ borderColor: brand.colors.border }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.key ? '' : 'border-transparent'
            }`}
            style={{
              color: activeTab === tab.key ? brand.colors.link : brand.colors.textLight,
              borderColor: activeTab === tab.key ? brand.colors.primary : 'transparent',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'originalSkus' && (
        <OriginalSkusTab
          brand={brand}
          Icons={Icons}
          skus={filteredSkus}
          stats={skuStats}
          search={search}
          onSearchChange={setSearch}
          productFilter={productFilter}
          productOptions={productOptions}
          onProductFilterChange={setProductFilter}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          getRowBg={getRowBg}
        />
      )}

      {activeTab === 'dimensionalModel' && (
        <DimensionalModelTab
          brand={brand}
          matrixData={matrixData}
          selectedProduct={selectedProduct}
          onProductChange={(code) => {
            setSelectedProduct(code);
            fetchMatrix(code);
          }}
          productOptions={productOptions}
          editableAdders={editableAdders}
          onAdderChange={handleAdderChange}
          computedPrices={computedMatrixPrices}
        />
      )}

      {activeTab === 'skuTranslator' && (
        <SkuTranslatorTab
          brand={brand}
          Icons={Icons}
          skus={translatorFilteredSkus}
          allSkus={skus}
          search={translatorSearch}
          onSearchChange={setTranslatorSearch}
          productFilter={translatorProductFilter}
          productOptions={productOptions}
          onProductFilterChange={setTranslatorProductFilter}
          showAllSkus={showAllSkus}
          onToggleShowAll={() => setShowAllSkus(v => !v)}
          selectedSkuId={selectedSkuId}
          onSelectSku={setSelectedSkuId}
          addersByProduct={addersByProduct}
        />
      )}

      {activeTab === 'anomalies' && (
        <AnomaliesTab
          brand={brand}
          anomalyData={anomalyData}
          summaryData={summaryData}
          sortField={anomalySortField}
          onSortFieldChange={setAnomalySortField}
        />
      )}

      {activeTab === 'optimizationGuide' && (
        <OptimizationGuideTab
          brand={brand}
          summaryData={summaryData}
          addersByProduct={addersByProduct}
          anomalyData={anomalyData}
          skus={skus}
        />
      )}

      {activeTab === 'summary' && (
        <ComparisonSummaryTab
          brand={brand}
          summaryData={summaryData}
          addersByProduct={addersByProduct}
        />
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// Tab 1: Original SKUs
// ══════════════════════════════════════════════════════════════

interface OriginalSkusTabProps {
  brand: BrandConfig;
  Icons: IconsType;
  skus: SkuRow[];
  stats: { total: number; dimensional: number; ancillary: number; perUnit: number; bundle: number };
  search: string;
  onSearchChange: (v: string) => void;
  productFilter: string;
  productOptions: { value: string; label: string }[];
  onProductFilterChange: (v: string) => void;
  typeFilter: string;
  onTypeFilterChange: (v: string) => void;
  getRowBg: (row: SkuRow) => string;
}

const OriginalSkusTab: React.FC<OriginalSkusTabProps> = ({
  brand,
  Icons,
  skus,
  stats,
  search,
  onSearchChange,
  productFilter,
  productOptions,
  onProductFilterChange,
  typeFilter,
  onTypeFilterChange,
  getRowBg,
}) => {
  const typeOptions = [
    { value: 'dimensional', label: 'Dimensional' },
    { value: 'ancillary', label: 'Ancillary' },
    { value: 'per-unit', label: 'Per-Unit' },
  ];

  return (
    <div>
      {/* Filters */}
      <SetupFilterBar
        brand={brand}
        Icons={Icons}
        searchValue={search}
        onSearchChange={onSearchChange}
        searchPlaceholder="Search pricing items..."
        categoryFilter={productFilter}
        categoryOptions={productOptions}
        onCategoryChange={onProductFilterChange}
        statusFilter={typeFilter}
        statusOptions={typeOptions}
        onStatusChange={onTypeFilterChange}
      />

      {/* Stats Bar */}
      <div
        className="flex gap-4 mb-4 px-4 py-2 rounded-lg text-sm"
        style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textLight }}
      >
        <span><strong>{stats.total}</strong> total</span>
        <span>|</span>
        <span><strong>{stats.dimensional}</strong> dimensional</span>
        <span>|</span>
        <span><strong>{stats.ancillary}</strong> ancillary</span>
        <span>|</span>
        <span><strong>{stats.perUnit}</strong> per-unit</span>
        {stats.bundle > 0 && (
          <>
            <span>|</span>
            <span><strong>{stats.bundle}</strong> bundle discounts</span>
          </>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto border rounded-lg" style={{ borderColor: brand.colors.border }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: brand.colors.bgLight }}>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>#</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Product</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Pricing Item</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Zone</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Job Size</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Service Level</th>
              <th className="px-3 py-2 text-right font-medium" style={{ color: brand.colors.textLight }}>Base Price</th>
              <th className="px-3 py-2 text-right font-medium" style={{ color: brand.colors.textLight }}>Non-LF Price</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Type</th>
            </tr>
          </thead>
          <tbody>
            {skus.map((row, idx) => (
              <tr
                key={row.id}
                className="border-t hover:bg-gray-50"
                style={{ borderColor: brand.colors.border, backgroundColor: getRowBg(row) }}
              >
                <td className="px-3 py-2" style={{ color: brand.colors.textMuted }}>{idx + 1}</td>
                <td className="px-3 py-2 font-medium" style={{ color: brand.colors.text }}>{row.productName ?? '—'}</td>
                <td className="px-3 py-2" style={{ color: brand.colors.text }}>{row.pricingItemName}</td>
                <td className="px-3 py-2" style={{ color: brand.colors.textLight }}>{row.zoneName ?? '—'}</td>
                <td className="px-3 py-2" style={{ color: brand.colors.textLight }}>{row.sizeTierName ?? '—'}</td>
                <td className="px-3 py-2" style={{ color: brand.colors.textLight }}>{row.serviceLevelName ?? '—'}</td>
                <td className="px-3 py-2 text-right font-mono" style={{ color: Number(row.basePrice) < 0 ? brand.colors.error : brand.colors.text }}>
                  ${Number(row.basePrice).toFixed(2)}
                </td>
                <td className="px-3 py-2 text-right font-mono" style={{ color: brand.colors.textLight }}>
                  {row.nonLawFirmPrice && Number(row.nonLawFirmPrice) > 0 ? `$${Number(row.nonLawFirmPrice).toFixed(2)}` : '—'}
                </td>
                <td className="px-3 py-2">
                  <span
                    className="inline-block px-2 py-0.5 rounded text-xs font-medium"
                    style={{
                      backgroundColor: row.isBundleDiscount
                        ? '#FEE2E2'
                        : row.priceType === 'dimensional'
                          ? '#DBEAFE'
                          : row.priceType === 'per-unit'
                            ? '#E0E7FF'
                            : '#F3F4F6',
                      color: row.isBundleDiscount
                        ? '#991B1B'
                        : row.priceType === 'dimensional'
                          ? '#1E40AF'
                          : row.priceType === 'per-unit'
                            ? '#3730A3'
                            : '#374151',
                    }}
                  >
                    {row.isBundleDiscount ? 'bundle' : row.priceType}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-2 text-sm" style={{ color: brand.colors.textMuted }}>
        Showing {skus.length} rows
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// Tab 2: Dimensional Model
// ══════════════════════════════════════════════════════════════

interface DimensionalModelTabProps {
  brand: BrandConfig;
  matrixData: MatrixResponse | null;
  selectedProduct: string;
  onProductChange: (code: string) => void;
  productOptions: { value: string; label: string }[];
  editableAdders: Record<string, Record<string, number>>;
  onAdderChange: (dimension: string, value: string, amount: number) => void;
  computedPrices: MatrixPrice[];
}

const DimensionalModelTab: React.FC<DimensionalModelTabProps> = ({
  brand,
  matrixData,
  selectedProduct,
  onProductChange,
  productOptions,
  editableAdders,
  onAdderChange,
  computedPrices,
}) => {
  if (!matrixData) {
    return <div style={{ color: brand.colors.textMuted }}>No data available</div>;
  }

  const hasAdders = Object.keys(editableAdders).length > 0;
  const matchCount = computedPrices.filter(p => p.match).length;
  const totalDimensional = computedPrices.length;

  // Determine model type description
  const hasZone = !!editableAdders.zone;
  const hasSize = !!editableAdders.size;
  const hasLevel = !!editableAdders.level;

  let modelNote = '';
  if (hasZone && hasSize && hasLevel) {
    modelNote = 'Fully additive — all dimensions are independently additive';
  } else if (hasZone && !hasSize && !hasLevel) {
    modelNote = 'Zone adders only — size×level has interaction';
  } else if (!hasAdders) {
    modelNote = 'No additive pattern — explicit lookup';
  }

  // Build grid: zones as outer groups, sizes within, levels as columns
  const zones = Array.from(new Set(computedPrices.map(p => p.zoneCode).filter(Boolean))) as string[];
  const sizes = Array.from(new Set(computedPrices.map(p => p.sizeTierCode).filter(Boolean))) as string[];
  const levels = Array.from(new Set(computedPrices.map(p => p.serviceLevelCode).filter(Boolean))) as string[];

  // Label lookups
  const zoneLabels = new Map(computedPrices.map(p => [p.zoneCode, p.zoneName]));
  const sizeLabels = new Map(computedPrices.map(p => [p.sizeTierCode, p.sizeTierName]));
  const levelLabels = new Map(computedPrices.map(p => [p.serviceLevelCode, p.serviceLevelName]));

  // Price lookup
  const getPriceForDims = (zone: string, size: string | null, level: string) => {
    return computedPrices.find(p =>
      p.zoneCode === zone &&
      (size ? p.sizeTierCode === size : !p.sizeTierCode) &&
      p.serviceLevelCode === level,
    );
  };

  const dimensionLabels: Record<string, string> = {
    'urban-court': 'Urban Court',
    'suburban-court': 'Suburban Court',
    'remote-court': 'Remote Court',
    'nationwide-court': 'Nationwide Court',
    'urban-area': 'Urban Area',
    'suburban-area': 'Suburban Area',
    'remote-area': 'Remote Area',
    'nationwide-area': 'Nationwide Area',
    'small-1-15': 'Small (1-15)',
    'medium-16-50': 'Medium (16-50)',
    'large-51-plus': 'Large (51+)',
    'routine': 'Routine',
    'standard': 'Standard',
    'priority': 'Priority',
    'urgent': 'Urgent',
    'on-demand': 'On Demand',
  };

  return (
    <div>
      {/* Product Selector */}
      <div className="mb-4">
        <select
          value={selectedProduct}
          onChange={(e) => onProductChange(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2"
          style={{
            borderColor: brand.colors.border,
            color: brand.colors.text,
          }}
        >
          {productOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="flex gap-6">
        {/* Left Panel: Adder Configuration */}
        <div className="w-[30%] min-w-[250px]">
          <div
            className="border rounded-lg p-4"
            style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgCard }}
          >
            <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
              Adder Configuration
            </h3>

            {matrixData.basePrice !== null && (
              <div className="mb-4">
                <span className="text-xs font-medium" style={{ color: brand.colors.textLight }}>Base Price</span>
                <div className="text-lg font-mono font-semibold" style={{ color: brand.colors.link }}>
                  ${matrixData.basePrice.toFixed(2)}
                </div>
              </div>
            )}

            {hasAdders ? (
              Object.entries(editableAdders).map(([dimension, values]) => (
                <div key={dimension} className="mb-4">
                  <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: brand.colors.textLight }}>
                    {dimension} Adders
                  </div>
                  {Object.entries(values).map(([value, amount]) => (
                    <div key={value} className="flex items-center justify-between py-1">
                      <span className="text-sm" style={{ color: brand.colors.text }}>
                        {dimensionLabels[value] || value}
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="text-sm" style={{ color: brand.colors.textMuted }}>+$</span>
                        <input
                          type="number"
                          value={amount}
                          onChange={(e) => onAdderChange(dimension, value, Number(e.target.value))}
                          className="w-16 px-2 py-1 border rounded text-sm text-right font-mono focus:outline-none focus:ring-1"
                          style={{ borderColor: brand.colors.border }}
                          step="5"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div className="text-sm py-4" style={{ color: brand.colors.textMuted }}>
                No additive pattern detected for this product. Prices are stored as explicit lookups.
              </div>
            )}

            {modelNote && (
              <div
                className="text-xs mt-3 p-2 rounded"
                style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textLight }}
              >
                {modelNote}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel: Price Matrix Grid */}
        <div className="flex-1 overflow-x-auto">
          <div
            className="border rounded-lg"
            style={{ borderColor: brand.colors.border }}
          >
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: brand.colors.bgLight }}>
                  <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>
                    {sizes.length > 0 ? 'Zone / Size' : 'Zone'}
                  </th>
                  {levels.map(level => (
                    <th key={level} className="px-3 py-2 text-center font-medium" style={{ color: brand.colors.textLight }}>
                      {levelLabels.get(level) ?? level}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {zones.map(zone =>
                  (sizes.length > 0 ? sizes : [null]).map((size, sizeIdx) => (
                    <tr
                      key={`${zone}-${size ?? 'none'}`}
                      className="border-t"
                      style={{ borderColor: brand.colors.border }}
                    >
                      <td className="px-3 py-2 font-medium" style={{ color: brand.colors.text }}>
                        {sizeIdx === 0 && (
                          <span className="font-semibold">{zoneLabels.get(zone) ?? zone}</span>
                        )}
                        {size && (
                          <>
                            {sizeIdx === 0 ? ' / ' : <span className="ml-4">{'  '}</span>}
                            <span style={{ color: brand.colors.textLight }}>
                              {sizeLabels.get(size) ?? size}
                            </span>
                          </>
                        )}
                      </td>
                      {levels.map(level => {
                        const price = getPriceForDims(zone, size, level);
                        if (!price) {
                          return (
                            <td key={level} className="px-3 py-2 text-center" style={{ color: brand.colors.textMuted }}>
                              —
                            </td>
                          );
                        }

                        const actual = Number(price.basePrice);
                        const computed = price.computedPrice;

                        return (
                          <td key={level} className="px-3 py-2 text-center">
                            <div className="font-mono text-sm" style={{ color: brand.colors.text }}>
                              ${actual.toFixed(2)}
                            </div>
                            {computed !== null && (
                              <div className="text-xs mt-0.5">
                                {price.match ? (
                                  <span style={{ color: brand.colors.success }}>
                                    &#10003;
                                  </span>
                                ) : (
                                  <span style={{ color: brand.colors.error }}>
                                    &#10007; exp ${computed.toFixed(2)}
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>

          {/* Footer Summary */}
          <div className="mt-3 text-sm" style={{ color: brand.colors.textLight }}>
            {hasAdders ? (
              <span>
                <strong style={{ color: matchCount === totalDimensional ? brand.colors.success : brand.colors.warning }}>
                  {matchCount}/{totalDimensional}
                </strong>
                {' '}match
                {matchCount === totalDimensional ? ' (fully additive)' : ''}
              </span>
            ) : (
              <span>{totalDimensional} dimensional prices (explicit lookup)</span>
            )}
          </div>

          {/* Ancillary Prices */}
          {matrixData.ancillaryPrices.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold mb-2" style={{ color: brand.colors.text }}>
                Ancillary & Per-Unit Items
              </h4>
              <div className="border rounded-lg overflow-hidden" style={{ borderColor: brand.colors.border }}>
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ backgroundColor: brand.colors.bgLight }}>
                      <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Item</th>
                      <th className="px-3 py-2 text-right font-medium" style={{ color: brand.colors.textLight }}>Price</th>
                      <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matrixData.ancillaryPrices.map(p => (
                      <tr key={p.id} className="border-t" style={{ borderColor: brand.colors.border }}>
                        <td className="px-3 py-2" style={{ color: brand.colors.text }}>{p.pricingItemName}</td>
                        <td className="px-3 py-2 text-right font-mono" style={{ color: Number(p.basePrice) < 0 ? brand.colors.error : brand.colors.text }}>
                          ${Number(p.basePrice).toFixed(2)}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className="inline-block px-2 py-0.5 rounded text-xs"
                            style={{
                              backgroundColor: p.isBundleDiscount ? '#FEE2E2' : '#F3F4F6',
                              color: p.isBundleDiscount ? '#991B1B' : '#374151',
                            }}
                          >
                            {p.isBundleDiscount ? 'bundle' : p.priceType}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// Tab 3: SKU Translator
// ══════════════════════════════════════════════════════════════

interface SkuTranslatorTabProps {
  brand: BrandConfig;
  Icons: IconsType;
  skus: SkuRow[];
  allSkus: SkuRow[];
  search: string;
  onSearchChange: (v: string) => void;
  productFilter: string;
  productOptions: { value: string; label: string }[];
  onProductFilterChange: (v: string) => void;
  showAllSkus: boolean;
  onToggleShowAll: () => void;
  selectedSkuId: string | null;
  onSelectSku: (id: string | null) => void;
  addersByProduct: AddersByProduct;
}

const SkuTranslatorTab: React.FC<SkuTranslatorTabProps> = ({
  brand,
  Icons,
  skus,
  allSkus,
  search,
  onSearchChange,
  productFilter,
  productOptions,
  onProductFilterChange,
  showAllSkus,
  onToggleShowAll,
  selectedSkuId,
  onSelectSku,
  addersByProduct,
}) => {
  const selectedSku = allSkus.find(s => s.id === selectedSkuId) ?? null;
  const productAdders = selectedSku?.productCode ? addersByProduct[selectedSku.productCode] : null;

  // Compute formula for the selected SKU
  const formula = React.useMemo(() => {
    if (!selectedSku || !productAdders || productAdders.basePrice === null) return null;

    const base = productAdders.basePrice;
    const zoneAdder = productAdders.adders.zone?.[selectedSku.zoneCode ?? ''] ?? 0;
    const sizeAdder = productAdders.adders.size?.[selectedSku.sizeTierCode ?? ''] ?? 0;
    const levelAdder = productAdders.adders.level?.[selectedSku.serviceLevelCode ?? ''] ?? 0;
    const computed = base + zoneAdder + sizeAdder + levelAdder;
    const actual = Number(selectedSku.basePrice);
    const match = Math.abs(computed - actual) < 0.01;

    return { base, zoneAdder, sizeAdder, levelAdder, computed, actual, match };
  }, [selectedSku, productAdders]);

  return (
    <div>
      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1">
          <SetupFilterBar
            brand={brand}
            Icons={Icons}
            searchValue={search}
            onSearchChange={onSearchChange}
            searchPlaceholder="Search SKUs by name, zone, size, level..."
            categoryFilter={productFilter}
            categoryOptions={productOptions}
            onCategoryChange={onProductFilterChange}
          />
        </div>
        <button
          onClick={onToggleShowAll}
          className="px-3 py-2 text-xs font-medium border rounded-lg whitespace-nowrap"
          style={{
            borderColor: brand.colors.border,
            backgroundColor: showAllSkus ? brand.colors.primary : 'transparent',
            color: showAllSkus ? brand.colors.onPrimary : brand.colors.textLight,
          }}
        >
          {showAllSkus ? 'Show all' : 'Dimensional only'}
        </button>
      </div>

      {/* Clickable Table */}
      <div className="overflow-x-auto border rounded-lg" style={{ borderColor: brand.colors.border }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: brand.colors.bgLight }}>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Product</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Zone</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Size</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Level</th>
              <th className="px-3 py-2 text-right font-medium" style={{ color: brand.colors.textLight }}>Price</th>
            </tr>
          </thead>
          <tbody>
            {skus.map(row => {
              const isSelected = row.id === selectedSkuId;
              return (
                <tr
                  key={row.id}
                  onClick={() => onSelectSku(isSelected ? null : row.id)}
                  className="border-t cursor-pointer transition-colors"
                  style={{
                    borderColor: brand.colors.border,
                    backgroundColor: isSelected ? `${brand.colors.primary}15` : undefined,
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = brand.colors.bgLight;
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = '';
                  }}
                >
                  <td className="px-3 py-2 font-medium" style={{ color: brand.colors.text }}>{row.productName ?? '—'}</td>
                  <td className="px-3 py-2" style={{ color: brand.colors.textLight }}>{row.zoneName ?? '—'}</td>
                  <td className="px-3 py-2" style={{ color: brand.colors.textLight }}>{row.sizeTierName ?? '—'}</td>
                  <td className="px-3 py-2" style={{ color: brand.colors.textLight }}>{row.serviceLevelName ?? '—'}</td>
                  <td className="px-3 py-2 text-right font-mono" style={{ color: brand.colors.text }}>
                    ${Number(row.basePrice).toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-2 text-sm" style={{ color: brand.colors.textMuted }}>
        {skus.length} SKUs — click a row to see its formula
      </div>

      {/* Formula Card */}
      {selectedSku && (
        <div
          className="mt-4 border rounded-lg p-5"
          style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgCard }}
        >
          <h4 className="text-sm font-semibold mb-1" style={{ color: brand.colors.text }}>
            {selectedSku.productName}
          </h4>
          <div className="text-xs mb-4" style={{ color: brand.colors.textLight }}>
            {[selectedSku.zoneName, selectedSku.sizeTierName, selectedSku.serviceLevelName].filter(Boolean).join(' / ')}
          </div>

          {productAdders && formula ? (
            <div>
              {/* Formula pills */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span
                  className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-mono font-medium"
                  style={{ backgroundColor: `${brand.colors.primary}15`, color: brand.colors.link }}
                >
                  Base ${formula.base.toFixed(2)}
                </span>
                {formula.zoneAdder !== 0 && (
                  <>
                    <span style={{ color: brand.colors.textMuted }}>+</span>
                    <span
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-mono font-medium"
                      style={{ backgroundColor: '#DBEAFE', color: '#1E40AF' }}
                    >
                      Zone {formula.zoneAdder >= 0 ? '+' : ''}${formula.zoneAdder.toFixed(2)}
                    </span>
                  </>
                )}
                {productAdders.adders.size && (
                  <>
                    <span style={{ color: brand.colors.textMuted }}>+</span>
                    <span
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-mono font-medium"
                      style={{ backgroundColor: '#E0E7FF', color: '#3730A3' }}
                    >
                      Size {formula.sizeAdder >= 0 ? '+' : ''}${formula.sizeAdder.toFixed(2)}
                    </span>
                  </>
                )}
                {productAdders.adders.level && (
                  <>
                    <span style={{ color: brand.colors.textMuted }}>+</span>
                    <span
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-mono font-medium"
                      style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
                    >
                      Level {formula.levelAdder >= 0 ? '+' : ''}${formula.levelAdder.toFixed(2)}
                    </span>
                  </>
                )}
                <span style={{ color: brand.colors.textMuted }}>=</span>
                <span
                  className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-mono font-semibold"
                  style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.text }}
                >
                  ${formula.computed.toFixed(2)}
                </span>
              </div>

              {/* Actual vs Computed */}
              <div className="flex items-center gap-3 text-sm">
                <span style={{ color: brand.colors.textLight }}>
                  Stored price: <strong className="font-mono">${formula.actual.toFixed(2)}</strong>
                </span>
                {formula.match ? (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
                    style={{ backgroundColor: '#DCFCE7', color: '#166534' }}
                  >
                    &#10003; Match
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
                    style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
                  >
                    &#10007; Variance: ${(formula.actual - formula.computed).toFixed(2)}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm py-2" style={{ color: brand.colors.textMuted }}>
              This product uses explicit lookup pricing — no additive formula available.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// Tab 4: Price Verification (formerly Anomalies)
// ══════════════════════════════════════════════════════════════

/** Convert pattern descriptions from clinical jargon to plain English. */
const humanizePattern = (description: string): string => {
  // Pattern: "N mismatches at +$X.XX all have dim=Value AND dim IN (V1, V2)"
  const match = description.match(
    /^(\d+) mismatches? at ([+-]?\$[\d.]+) all have (.+)$/i,
  );
  if (!match) return description;

  const [, count, amount, conditions] = match;

  // Parse conditions like "size=Medium AND level IN (Routine, Standard)"
  const parts = conditions.split(/\s+AND\s+/i).map(part => {
    const eqMatch = part.match(/^(\w+)=(.+)$/);
    if (eqMatch) {
      const dim = eqMatch[1] === 'size' ? 'size' : eqMatch[1] === 'level' ? 'service level' : eqMatch[1];
      return `${dim} is ${eqMatch[2]}`;
    }
    const inMatch = part.match(/^(\w+)\s+IN\s+\((.+)\)$/i);
    if (inMatch) {
      const dim = inMatch[1] === 'size' ? 'size' : inMatch[1] === 'level' ? 'service level' : inMatch[1];
      return `across ${inMatch[2]} ${dim}s`;
    }
    return part;
  });

  return `${count} prices are consistently ${amount} ${amount.startsWith('+') ? 'higher' : 'lower'} than the formula predicts when ${parts.join(' ')}`;
};

interface AnomaliesTabProps {
  brand: BrandConfig;
  anomalyData: AnomalyResponse | null;
  summaryData: SummaryResponse | null;
  sortField: 'product' | 'variance';
  onSortFieldChange: (field: 'product' | 'variance') => void;
}

const AnomaliesTab: React.FC<AnomaliesTabProps> = ({ brand, anomalyData, summaryData: _summaryData, sortField, onSortFieldChange }) => {
  const mismatches = anomalyData?.mismatches ?? [];
  const stats = anomalyData?.stats;
  const patterns = anomalyData?.patterns ?? [];

  const sortedMismatches = React.useMemo(() => {
    const sorted = [...mismatches];
    if (sortField === 'product') {
      sorted.sort((a, b) => (a.productName ?? '').localeCompare(b.productName ?? ''));
    } else {
      sorted.sort((a, b) => Math.abs(b.varianceDollars) - Math.abs(a.varianceDollars));
    }
    return sorted;
  }, [mismatches, sortField]);

  const matchedCount = stats ? stats.totalDimensionalRows - stats.totalMismatches : 0;
  const matchRate = stats && stats.totalDimensionalRows > 0
    ? ((matchedCount / stats.totalDimensionalRows) * 100).toFixed(1)
    : '0';

  if (!anomalyData || !stats) {
    return <div style={{ color: brand.colors.textMuted }}>No data available</div>;
  }

  if (mismatches.length === 0) {
    return (
      <div
        className="border-2 rounded-lg p-6 text-center"
        style={{ borderColor: brand.colors.success, backgroundColor: '#F0FDF4' }}
      >
        <div className="text-lg font-semibold mb-1" style={{ color: brand.colors.success }}>
          &#10003; All {stats.totalDimensionalRows} prices match their formulas exactly
        </div>
        <div className="text-sm" style={{ color: brand.colors.textLight }}>
          Every stored price equals what the formula (starting price + adjustments) computes.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Narrative Header */}
      <div
        className="border rounded-lg p-5 mb-5"
        style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgCard }}
      >
        <div className="text-sm leading-relaxed" style={{ color: brand.colors.textLight }}>
          We checked <strong style={{ color: brand.colors.text }}>{stats.totalDimensionalRows}</strong> prices
          across <strong style={{ color: brand.colors.text }}>{stats.productsAffected}</strong> products
          against their formulas (starting price + adjustments).{' '}
          <strong style={{ color: brand.colors.success }}>{matchedCount}</strong> matched
          perfectly (<strong>{matchRate}%</strong>).{' '}
          <strong style={{ color: brand.colors.error }}>{stats.totalMismatches}</strong> prices
          differ from what the formula computes.
        </div>
        <div className="text-sm mt-3" style={{ color: brand.colors.textMuted }}>
          These differences are stored prices that don&apos;t follow the pattern. They may be
          intentional overrides, data entry errors, or signs that the adjustment amounts need tuning.
        </div>
        {stats.medianMatchedDate && stats.medianMismatchDate && (() => {
          const matchedMs = new Date(stats.medianMatchedDate).getTime();
          const mismatchMs = new Date(stats.medianMismatchDate).getTime();
          const diffMonths = Math.round((matchedMs - mismatchMs) / (1000 * 60 * 60 * 24 * 30.44));
          if (diffMonths >= 2) {
            return (
              <div className="text-sm mt-2" style={{ color: brand.colors.warning }}>
                Mismatched prices were last modified a median of <strong>{diffMonths} months</strong> earlier
                than matching prices, suggesting they may predate the current formula structure.
              </div>
            );
          }
          return null;
        })()}
      </div>

      {/* Pattern Callouts */}
      {patterns.length > 0 && (
        <div className="mb-5">
          <h4 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
            Patterns in the differences
          </h4>
          {patterns.map((pattern) => (
            <div
              key={pattern.description}
              className="border rounded-lg p-4 mb-3"
              style={{
                borderColor: brand.colors.warning,
                backgroundColor: '#FFFBEB',
              }}
            >
              <p className="text-sm mb-2" style={{ color: '#78350F' }}>
                {humanizePattern(pattern.description)}
              </p>
              <p className="text-xs" style={{ color: '#92400E' }}>
                A consistent offset like this is often an intentional surcharge or a sign that an adjustment amount needs updating.
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Sort Controls */}
      <div className="flex gap-2 mb-3">
        <button
          onClick={() => onSortFieldChange('product')}
          className="px-3 py-1.5 text-xs font-medium border rounded-lg"
          style={{
            borderColor: brand.colors.border,
            backgroundColor: sortField === 'product' ? brand.colors.primary : 'transparent',
            color: sortField === 'product' ? brand.colors.onPrimary : brand.colors.textLight,
          }}
        >
          Group by Product
        </button>
        <button
          onClick={() => onSortFieldChange('variance')}
          className="px-3 py-1.5 text-xs font-medium border rounded-lg"
          style={{
            borderColor: brand.colors.border,
            backgroundColor: sortField === 'variance' ? brand.colors.primary : 'transparent',
            color: sortField === 'variance' ? brand.colors.onPrimary : brand.colors.textLight,
          }}
        >
          Sort by Difference
        </button>
      </div>

      {/* Mismatch Table */}
      <div className="overflow-x-auto border rounded-lg" style={{ borderColor: brand.colors.border }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: brand.colors.bgLight }}>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Product</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Zone</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Size</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Level</th>
              <th className="px-3 py-2 text-right font-medium" style={{ color: brand.colors.textLight }}>Stored Price</th>
              <th className="px-3 py-2 text-right font-medium" style={{ color: brand.colors.textLight }}>Formula Price</th>
              <th className="px-3 py-2 text-right font-medium" style={{ color: brand.colors.textLight }}>Difference ($)</th>
              <th className="px-3 py-2 text-right font-medium" style={{ color: brand.colors.textLight }}>Difference (%)</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Last Modified</th>
            </tr>
          </thead>
          <tbody>
            {sortedMismatches.map((row) => (
              <tr key={`${row.productCode}-${row.zoneCode}-${row.sizeTierCode}-${row.serviceLevelCode}`} className="border-t" style={{ borderColor: brand.colors.border }}>
                <td className="px-3 py-2 font-medium" style={{ color: brand.colors.text }}>{row.productName ?? '—'}</td>
                <td className="px-3 py-2" style={{ color: brand.colors.textLight }}>{row.zoneName ?? '—'}</td>
                <td className="px-3 py-2" style={{ color: brand.colors.textLight }}>{row.sizeTierName ?? '—'}</td>
                <td className="px-3 py-2" style={{ color: brand.colors.textLight }}>{row.serviceLevelName ?? '—'}</td>
                <td className="px-3 py-2 text-right font-mono" style={{ color: brand.colors.text }}>
                  ${row.actualPrice.toFixed(2)}
                </td>
                <td className="px-3 py-2 text-right font-mono" style={{ color: brand.colors.textLight }}>
                  ${row.expectedPrice.toFixed(2)}
                </td>
                <td className="px-3 py-2 text-right font-mono font-medium" style={{ color: brand.colors.error }}>
                  {row.varianceDollars >= 0 ? '+' : ''}${row.varianceDollars.toFixed(2)}
                </td>
                <td className="px-3 py-2 text-right font-mono" style={{ color: brand.colors.error }}>
                  {row.variancePercent >= 0 ? '+' : ''}{row.variancePercent.toFixed(1)}%
                </td>
                <td className="px-3 py-2 text-sm" style={{ color: brand.colors.textLight }}>
                  {row.lastModifiedSource
                    ? new Date(row.lastModifiedSource).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-2 text-sm" style={{ color: brand.colors.textMuted }}>
        Showing {sortedMismatches.length} prices that differ from formula | {matchedCount} matched perfectly
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// Tab 5: Optimization Guide
// ══════════════════════════════════════════════════════════════

/** Minimum dimensional rows for a product to be worth recommending formula pricing. */
const MIN_DIMENSIONAL_ROWS_FOR_RECOMMENDATION = 6;

interface OptimizationGuideTabProps {
  brand: BrandConfig;
  summaryData: SummaryResponse | null;
  addersByProduct: AddersByProduct;
  anomalyData: AnomalyResponse | null;
  skus: SkuRow[];
}

const OptimizationGuideTab: React.FC<OptimizationGuideTabProps> = ({ brand, summaryData, addersByProduct, anomalyData, skus }) => {
  // Section A: Pick the first optimized product for the worked example
  const workedExample = React.useMemo(() => {
    if (!summaryData) return null;
    const optimized = summaryData.data
      .filter(p => p.adderCount > 0)
      .sort((a, b) => b.optimizedRows - a.optimizedRows);
    if (optimized.length === 0) return null;

    const product = optimized[0];
    const entry = addersByProduct[product.productCode];
    if (!entry || entry.basePrice == null) return null;

    const dimensions: { dimension: string; label: string; values: { name: string; amount: number }[] }[] = [];
    const dimLabelMap: Record<string, string> = { zone: 'Zone', size: 'Size', level: 'Level' };

    for (const [dim, values] of Object.entries(entry.adders)) {
      const items = Object.entries(values).map(([name, amount]) => ({ name, amount }));
      dimensions.push({ dimension: dim, label: dimLabelMap[dim] || dim, values: items });
    }

    // Pick first non-zero adder from each dimension for the example calculation
    const exampleAdders: { label: string; name: string; amount: number }[] = [];
    for (const dim of dimensions) {
      const nonZero = dim.values.find(v => v.amount !== 0);
      if (nonZero) {
        exampleAdders.push({ label: dim.label, name: nonZero.name, amount: nonZero.amount });
      }
    }

    const exampleTotal = entry.basePrice + exampleAdders.reduce((sum, a) => sum + a.amount, 0);
    const totalAdderCount = dimensions.reduce((sum, d) => sum + d.values.length, 0);

    return {
      productName: product.productName,
      basePrice: entry.basePrice,
      dimensions,
      exampleAdders,
      exampleTotal,
      dimensionalRows: product.dimensionalRows,
      adderCount: totalAdderCount,
    };
  }, [summaryData, addersByProduct]);

  // Section B: Unoptimized products with enough dimensional rows to be worth simplifying
  // + analyze their dimension breakdown from actual SKU data
  const recommendations = React.useMemo(() => {
    if (!summaryData) return [];

    const candidates = summaryData.data
      .filter(p => p.adderCount === 0 && p.dimensionalRows >= MIN_DIMENSIONAL_ROWS_FOR_RECOMMENDATION)
      .sort((a, b) => b.dimensionalRows - a.dimensionalRows);

    return candidates.map(product => {
      // Analyze the actual dimensions this product uses from SKU data
      const productSkus = skus.filter(
        s => s.productCode === product.productCode && s.priceType === 'dimensional' && !s.isBundleDiscount,
      );
      const zones = new Set(productSkus.map(s => s.zoneName).filter(Boolean));
      const sizes = new Set(productSkus.map(s => s.sizeTierName).filter(Boolean));
      const levels = new Set(productSkus.map(s => s.serviceLevelName).filter(Boolean));

      // Build a human-readable dimension breakdown like "4 zones x 4 levels"
      const dimParts: string[] = [];
      if (zones.size > 0) dimParts.push(`${zones.size} zone${zones.size > 1 ? 's' : ''}`);
      if (sizes.size > 0) dimParts.push(`${sizes.size} size${sizes.size > 1 ? 's' : ''}`);
      if (levels.size > 0) dimParts.push(`${levels.size} level${levels.size > 1 ? 's' : ''}`);

      // Potential adder count: sum of unique values across dimensions (minus 1 base per dimension)
      const potentialAdders = Math.max(0, zones.size) + Math.max(0, sizes.size) + Math.max(0, levels.size);

      return {
        ...product,
        zones: Array.from(zones),
        sizes: Array.from(sizes),
        levels: Array.from(levels),
        dimensionBreakdown: dimParts.join(' x '),
        potentialAdders,
      };
    });
  }, [summaryData, skus]);

  // Section C: Top mismatches for anomaly resolution examples
  const topMismatches = React.useMemo(() => {
    if (!anomalyData) return [];
    return [...anomalyData.mismatches]
      .sort((a, b) => Math.abs(b.varianceDollars) - Math.abs(a.varianceDollars))
      .slice(0, 3);
  }, [anomalyData]);

  if (!summaryData) {
    return <div style={{ color: brand.colors.textMuted }}>No data available</div>;
  }

  return (
    <div>
      {/* Section A: How Formula Pricing Works — worked example */}
      {workedExample && (
        <div
          className="border rounded-lg p-5 mb-6"
          style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgCard }}
        >
          <h4 className="text-sm font-semibold mb-4" style={{ color: brand.colors.text }}>
            How formula pricing works: {workedExample.productName}
          </h4>

          <div className="text-sm mb-4" style={{ color: brand.colors.textLight }}>
            Instead of storing {workedExample.dimensionalRows} individual prices, this product uses
            1 starting price + {workedExample.adderCount} adjustments. Each price is calculated
            automatically.
          </div>

          {/* Starting price */}
          <div className="mb-3">
            <span className="text-sm" style={{ color: brand.colors.textLight }}>Starting price: </span>
            <span className="font-mono font-semibold text-sm" style={{ color: brand.colors.link }}>
              ${workedExample.basePrice.toFixed(2)}
            </span>
          </div>

          {/* Dimension adders */}
          {workedExample.dimensions.map(dim => (
            <div key={dim.dimension} className="mb-3">
              <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: brand.colors.textLight }}>
                {dim.label} adjustments
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {dim.values.map(v => (
                  <span key={v.name} className="text-sm font-mono" style={{ color: brand.colors.text }}>
                    {v.name}{' '}
                    <span style={{ color: v.amount === 0 ? brand.colors.textMuted : brand.colors.text }}>
                      {v.amount >= 0 ? '+' : ''}${v.amount.toFixed(2)}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          ))}

          {/* Example calculation */}
          {workedExample.exampleAdders.length > 0 && (
            <div
              className="rounded-lg p-4 mt-4 font-mono text-sm"
              style={{ backgroundColor: brand.colors.bgLight, border: `1px solid ${brand.colors.border}` }}
            >
              <div className="font-semibold mb-2" style={{ color: brand.colors.text, fontFamily: 'inherit' }}>
                Example: {workedExample.exampleAdders.map(a => a.name).join(' + ')}
              </div>
              <div style={{ color: brand.colors.textLight }}>
                = ${workedExample.basePrice.toFixed(2)}
                {workedExample.exampleAdders.map((a) => (
                  <span key={a.name}> + ${a.amount.toFixed(2)}</span>
                ))}
                {' '}={' '}
                <strong style={{ color: brand.colors.link }}>${workedExample.exampleTotal.toFixed(2)}</strong>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Section B: Recommended simplifications */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
          Recommended for formula pricing
        </h4>

        {recommendations.length === 0 ? (
          <div
            className="border rounded-lg p-4 text-sm"
            style={{ borderColor: brand.colors.border, color: brand.colors.textLight }}
          >
            All products with dimensional prices already use formula pricing.
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {recommendations.map(product => (
              <div
                key={product.productCode}
                className="border rounded-lg p-5"
                style={{ borderColor: brand.colors.warning, backgroundColor: '#FFFBEB' }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="text-sm font-semibold" style={{ color: brand.colors.text }}>
                    {product.productName}
                  </div>
                  <span
                    className="inline-block px-2 py-0.5 rounded text-xs font-medium"
                    style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
                  >
                    {product.dimensionalRows} prices
                  </span>
                </div>

                <div className="text-sm mb-3" style={{ color: '#78350F' }}>
                  Currently stores {product.dimensionalRows} individual prices
                  across <strong>{product.dimensionBreakdown}</strong>.
                  {product.potentialAdders > 0 && (
                    <> With formula pricing, this could become 1 starting price + ~{product.potentialAdders} adjustments.</>
                  )}
                </div>

                {/* Show the actual dimension values */}
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs" style={{ color: '#92400E' }}>
                  {product.zones.length > 0 && (
                    <div>
                      <span className="font-semibold uppercase tracking-wide">Zones: </span>
                      {product.zones.join(', ')}
                    </div>
                  )}
                  {product.sizes.length > 0 && (
                    <div>
                      <span className="font-semibold uppercase tracking-wide">Sizes: </span>
                      {product.sizes.join(', ')}
                    </div>
                  )}
                  {product.levels.length > 0 && (
                    <div>
                      <span className="font-semibold uppercase tracking-wide">Levels: </span>
                      {product.levels.join(', ')}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section C: Differences to Resolve */}
      <div>
        <h4 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
          Differences to resolve
        </h4>

        {!anomalyData || anomalyData.mismatches.length === 0 ? (
          <div
            className="border rounded-lg p-4 text-sm"
            style={{ borderColor: brand.colors.success, backgroundColor: '#F0FDF4', color: brand.colors.textLight }}
          >
            All formula prices match stored prices exactly — no differences to resolve.
          </div>
        ) : (
          <div
            className="border rounded-lg p-5"
            style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgCard }}
          >
            <div className="text-sm mb-4" style={{ color: brand.colors.textLight }}>
              <strong style={{ color: brand.colors.error }}>{anomalyData.stats.totalMismatches}</strong> stored
              prices don&apos;t match their formulas. Resolving these would make formula pricing 100% accurate.
            </div>

            {topMismatches.map((row, idx) => (
              <div
                key={`${row.productCode}-${row.zoneCode}-${row.sizeTierCode}-${row.serviceLevelCode}`}
                className="flex items-start gap-3 py-2"
                style={{ borderTop: idx > 0 ? `1px solid ${brand.colors.border}` : undefined }}
              >
                <div className="flex-1">
                  <div className="text-sm font-medium" style={{ color: brand.colors.text }}>
                    {row.productName} — {[row.zoneName, row.sizeTierName, row.serviceLevelName].filter(Boolean).join(', ')}
                  </div>
                  <div className="text-sm mt-0.5" style={{ color: brand.colors.textLight }}>
                    Stored:{' '}
                    <span className="font-mono">${row.actualPrice.toFixed(2)}</span>
                    {'  '}&#8594;{'  '}Formula:{' '}
                    <span className="font-mono">${row.expectedPrice.toFixed(2)}</span>
                    {'  '}
                    <span className="font-mono" style={{ color: brand.colors.error }}>
                      (difference: {row.varianceDollars >= 0 ? '+' : ''}${row.varianceDollars.toFixed(2)})
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {anomalyData.stats.totalMismatches > 3 && (
              <div className="text-xs mt-3" style={{ color: brand.colors.textMuted }}>
                See Price Verification tab for all {anomalyData.stats.totalMismatches} differences.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// Tab 6: Comparison Summary
// ══════════════════════════════════════════════════════════════

interface ComparisonSummaryTabProps {
  brand: BrandConfig;
  summaryData: SummaryResponse | null;
  addersByProduct: AddersByProduct;
}

interface BarSegment {
  label: string;
  count: number;
  color: string;
}

const ComparisonSummaryTab: React.FC<ComparisonSummaryTabProps> = ({ brand, summaryData, addersByProduct }) => {
  const stats = summaryData?.stats ?? null;

  // Sort products: those with adders first (by optimizedRows desc), then without adders (by totalRows desc)
  const sortedProducts = React.useMemo(() => {
    const products = summaryData?.data ?? [];
    const withAdders = products.filter(p => p.adderCount > 0).sort((a, b) => b.optimizedRows - a.optimizedRows);
    const withoutAdders = products.filter(p => p.adderCount === 0).sort((a, b) => b.totalRows - a.totalRows);
    return { withAdders, withoutAdders };
  }, [summaryData?.data]);

  // Pick the first optimized product for the concrete example
  const exampleProduct = React.useMemo(() => {
    if (sortedProducts.withAdders.length === 0) return null;
    const product = sortedProducts.withAdders[0];
    const entry = addersByProduct[product.productCode];
    if (!entry || entry.basePrice == null) return null;

    // Find first non-zero adder to use as the example adjustment
    let exampleDimension = '';
    let exampleValue = '';
    let exampleAdder = 0;
    for (const [dim, values] of Object.entries(entry.adders)) {
      for (const [val, amount] of Object.entries(values)) {
        if (amount !== 0) {
          exampleDimension = dim;
          exampleValue = val;
          exampleAdder = amount;
          break;
        }
      }
      if (exampleAdder !== 0) break;
    }
    if (exampleAdder === 0) return null;

    const totalAdders = Object.values(entry.adders).reduce(
      (sum, vals) => sum + Object.keys(vals).length, 0,
    );

    const dimLabel = exampleDimension === 'zone' ? 'zone'
      : exampleDimension === 'size' ? 'size tier'
      : exampleDimension === 'level' ? 'service level'
      : exampleDimension;

    return {
      productName: product.productName,
      basePrice: entry.basePrice,
      adderAmount: exampleAdder,
      computedPrice: entry.basePrice + exampleAdder,
      dimensionLabel: dimLabel,
      dimensionValue: exampleValue,
      dimensionalRows: product.dimensionalRows,
      adderCount: totalAdders,
    };
  }, [sortedProducts.withAdders, addersByProduct]);

  // Bar segments
  const segments: BarSegment[] = React.useMemo(() => {
    if (!stats) return [];
    return [
      { label: 'Optimized Dimensional', count: stats.totalOptimizedDimensional, color: brand.colors.success },
      { label: 'Unoptimized Dimensional', count: stats.totalUnoptimizedDimensional, color: brand.colors.warning },
      { label: 'Bundle Discounts', count: stats.totalBundleDiscount, color: '#818CF8' },
      { label: 'Ancillary', count: stats.totalAncillary, color: '#9CA3AF' },
      { label: 'Per-Unit', count: stats.totalPerUnit, color: brand.colors.info },
    ].filter(s => s.count > 0);
  }, [stats, brand.colors]);

  if (!summaryData || !stats) {
    return <div style={{ color: brand.colors.textMuted }}>No data available</div>;
  }

  // Describe kept-as-is breakdown for a product
  const keptAsIsLabel = (p: SummaryRow): string => {
    const parts: string[] = [];
    const unoptDim = p.dimensionalRows - p.optimizedRows;
    if (unoptDim > 0) parts.push(`${unoptDim} dim`);
    if (p.bundleDiscountRows > 0) parts.push(`${p.bundleDiscountRows} bundle`);
    if (p.ancillaryRows > 0) parts.push(`${p.ancillaryRows} anc`);
    if (p.perUnitRows > 0) parts.push(`${p.perUnitRows} per`);
    return parts.join(', ') || '—';
  };

  return (
    <div>
      {/* Section A: Hero Metric */}
      <div
        className="border rounded-lg p-5 mb-6"
        style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgCard }}
      >
        {/* Part 1: Plain-English narrative */}
        <div className="text-sm leading-relaxed mb-4" style={{ color: brand.colors.textLight }}>
          Each product has prices that vary by zone, size, and service level.
          For <strong style={{ color: brand.colors.text }}>{stats.productsWithAdders}</strong> products,
          every price can be calculated from a starting price plus adjustments
          — replacing <strong style={{ color: brand.colors.success }}>{stats.totalOptimizedDimensional}</strong> individually
          stored prices.
        </div>

        {/* Part 2: Concrete example */}
        {exampleProduct && (
          <div
            className="rounded-lg p-4 mb-4 font-mono text-sm"
            style={{ backgroundColor: brand.colors.bgLight, border: `1px solid ${brand.colors.border}` }}
          >
            <div className="font-semibold mb-2" style={{ color: brand.colors.text, fontFamily: 'inherit' }}>
              Example: {exampleProduct.productName}
            </div>
            <div className="flex flex-col gap-0.5" style={{ color: brand.colors.textLight }}>
              <div>
                Starting price:{' '}
                <span style={{ color: brand.colors.text }}>${exampleProduct.basePrice.toFixed(2)}</span>
              </div>
              <div>
                + <span style={{ fontFamily: 'inherit' }}>{exampleProduct.dimensionValue} {exampleProduct.dimensionLabel}</span> adjustment:{' '}
                <span style={{ color: brand.colors.text }}>+${exampleProduct.adderAmount.toFixed(2)}</span>
              </div>
              <div style={{ borderTop: `1px solid ${brand.colors.border}`, paddingTop: '4px', marginTop: '2px' }}>
                = <span style={{ fontFamily: 'inherit' }}>{exampleProduct.dimensionValue}</span> price:{' '}
                <strong style={{ color: brand.colors.link }}>${exampleProduct.computedPrice.toFixed(2)}</strong>
              </div>
            </div>
            <div
              className="mt-3 text-xs"
              style={{ color: brand.colors.textMuted, fontFamily: 'inherit' }}
            >
              This replaces {exampleProduct.dimensionalRows} stored prices with 1 starting price + {exampleProduct.adderCount} adjustments.
            </div>
          </div>
        )}

        {/* Part 3: Summary line */}
        <div className="text-sm" style={{ color: brand.colors.textLight }}>
          <strong style={{ color: brand.colors.text }}>{stats.totalProducts}</strong> products,{' '}
          <strong style={{ color: brand.colors.text }}>{stats.totalRows}</strong> prices total.{' '}
          <span style={{ color: brand.colors.textMuted }}>
            {stats.productsWithoutAdders + stats.productsFlatOnly} products unchanged ({stats.totalLookupRows} prices stored individually).
          </span>
        </div>
      </div>

      {/* Section B: Segmented Bar — "Where the Rows Went" */}
      <div
        className="border rounded-lg p-5 mb-6"
        style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgCard }}
      >
        <h4 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
          Where the {stats.totalRows} Rows Went
        </h4>

        {/* Stacked bar */}
        <div className="flex rounded overflow-hidden h-8 mb-3">
          {segments.map(seg => {
            const pct = (seg.count / stats.totalRows) * 100;
            return (
              <div
                key={seg.label}
                style={{
                  width: `${pct}%`,
                  backgroundColor: seg.color,
                  minWidth: seg.count > 0 ? '2px' : 0,
                }}
                className="flex items-center justify-center text-xs font-semibold text-white"
                title={`${seg.label}: ${seg.count}`}
              >
                {pct > 8 ? seg.count : ''}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-x-5 gap-y-1">
          {segments.map(seg => (
            <div key={seg.label} className="flex items-center gap-1.5 text-xs" style={{ color: brand.colors.textLight }}>
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: seg.color }}
              />
              {seg.label}: <strong style={{ color: brand.colors.text }}>{seg.count}</strong>
            </div>
          ))}
        </div>
      </div>

      {/* Section C: Per-Product Table */}
      <div className="border rounded-lg overflow-x-auto mb-6" style={{ borderColor: brand.colors.border }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: brand.colors.bgLight }}>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Product</th>
              <th className="px-3 py-2 text-center font-medium" style={{ color: brand.colors.textLight }}>Total</th>
              <th className="px-3 py-2 text-center font-medium" style={{ color: brand.colors.textLight }}>Optimized</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Kept As-Is</th>
              <th className="px-3 py-2 text-left font-medium" style={{ color: brand.colors.textLight }}>Model</th>
              <th className="px-3 py-2 text-center font-medium" style={{ color: brand.colors.textLight }}>Compression</th>
              <th className="px-3 py-2 text-center font-medium" style={{ color: brand.colors.textLight }}>Adders</th>
            </tr>
          </thead>
          <tbody>
            {sortedProducts.withAdders.map(product => (
              <tr key={product.productCode} className="border-t" style={{ borderColor: brand.colors.border }}>
                <td className="px-3 py-2 font-medium" style={{ color: brand.colors.text }}>
                  {product.productName}
                </td>
                <td className="px-3 py-2 text-center" style={{ color: brand.colors.text }}>
                  {product.totalRows}
                </td>
                <td className="px-3 py-2 text-center" style={{ color: brand.colors.success }}>
                  {product.optimizedRows > 0 ? `${product.optimizedRows} dim` : '—'}
                </td>
                <td className="px-3 py-2 text-xs" style={{ color: brand.colors.textLight }}>
                  {keptAsIsLabel(product)}
                </td>
                <td className="px-3 py-2">
                  <span
                    className="inline-block px-2 py-0.5 rounded text-xs font-medium"
                    style={{
                      backgroundColor: product.modelType === 'Full additive' ? '#DCFCE7'
                        : product.modelType === 'Zone additive' ? '#FEF3C7'
                        : '#F3F4F6',
                      color: product.modelType === 'Full additive' ? '#166534'
                        : product.modelType === 'Zone additive' ? '#92400E'
                        : '#374151',
                    }}
                  >
                    {product.modelType}
                  </span>
                </td>
                <td className="px-3 py-2 text-center font-mono text-xs" style={{ color: brand.colors.text }}>
                  {product.compressionLabel || '—'}
                </td>
                <td className="px-3 py-2 text-center font-mono" style={{ color: brand.colors.textLight }}>
                  {product.adderCount}
                </td>
              </tr>
            ))}
            {/* Separator between optimized and non-optimized products */}
            {sortedProducts.withAdders.length > 0 && sortedProducts.withoutAdders.length > 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-1 text-xs font-medium"
                  style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textMuted }}
                >
                  No adders configured
                </td>
              </tr>
            )}
            {sortedProducts.withoutAdders.map(product => (
              <tr key={product.productCode} className="border-t" style={{ borderColor: brand.colors.border }}>
                <td className="px-3 py-2 font-medium" style={{ color: brand.colors.text }}>
                  {product.productName}
                </td>
                <td className="px-3 py-2 text-center" style={{ color: brand.colors.text }}>
                  {product.totalRows}
                </td>
                <td className="px-3 py-2 text-center" style={{ color: brand.colors.textMuted }}>
                  —
                </td>
                <td className="px-3 py-2 text-xs" style={{ color: brand.colors.textLight }}>
                  {keptAsIsLabel(product)}
                </td>
                <td className="px-3 py-2">
                  <span
                    className="inline-block px-2 py-0.5 rounded text-xs font-medium"
                    style={{ backgroundColor: '#F3F4F6', color: '#374151' }}
                  >
                    {product.modelType}
                  </span>
                </td>
                <td className="px-3 py-2 text-center" style={{ color: brand.colors.textMuted }}>
                  —
                </td>
                <td className="px-3 py-2 text-center" style={{ color: brand.colors.textMuted }}>
                  —
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Section D: Config Efficiency */}
      <div
        className="border rounded-lg p-5"
        style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgCard }}
      >
        <h4 className="text-sm font-semibold mb-4" style={{ color: brand.colors.text }}>
          Config Efficiency
        </h4>

        {/* Side-by-side metric cards */}
        <div className="flex items-center gap-4 mb-4">
          <div
            className="flex-1 border rounded-lg p-4 text-center"
            style={{ borderColor: brand.colors.border }}
          >
            <div className="text-3xl font-semibold font-mono" style={{ color: brand.colors.text }}>
              {stats.totalRows}
            </div>
            <div className="text-xs font-medium mt-1" style={{ color: brand.colors.textLight }}>
              individually stored prices
            </div>
          </div>

          <div className="text-xl font-semibold" style={{ color: brand.colors.textMuted }}>
            &rarr;
          </div>

          <div
            className="flex-1 border rounded-lg p-4 text-center"
            style={{ borderColor: brand.colors.primary, borderWidth: '2px' }}
          >
            <div className="text-3xl font-semibold font-mono" style={{ color: brand.colors.link }}>
              {stats.totalLookupRows}
            </div>
            <div className="text-xs font-medium mt-1" style={{ color: brand.colors.textLight }}>
              stored individually
            </div>
            <div className="text-xs mt-2" style={{ color: brand.colors.success }}>
              {stats.totalOptimizedDimensional} prices now calculated automatically
            </div>
            <div className="text-xs mt-1" style={{ color: brand.colors.textMuted }}>
              from {stats.productsWithAdders} starting prices + {stats.totalAdders} adjustments
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-xs" style={{ color: brand.colors.textMuted }}>
          {stats.productsWithAdders} products optimized | {stats.productsWithoutAdders} could benefit from adders | {stats.productsFlatOnly} flat-rate only
        </div>
      </div>
    </div>
  );
};

export default SetupPricingLab;
